import { useState } from "react";
import {
  Box,
  Card,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Collapse,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import {
  AddCircleOutline as PlusCircle,
  ExpandMore as ChevronDown,
  ChevronRight,
  Delete as Trash2,
  RestartAlt,
} from "@mui/icons-material";
import { CriteriaRow } from "./CriteriaRow";
import {
  GroupNode,
  CriterionNode,
  isGroupNode,
  isCriterionNode,
  createGroupNode,
  createCriterionNode,
} from "./nested-dsl-utils";

interface GroupCardProps {
  group: GroupNode;
  studyId?: string;
  platformType?: "registry" | "study";
  depth?: number;
  onChange: (updated: GroupNode) => void;
  onDelete?: () => void;
  dragHandleProps?: any;
  showReset?: boolean;
  onReset?: () => void;
}

export function GroupCard({
  group,
  studyId,
  platformType,
  depth = 0,
  onChange,
  onDelete,
  dragHandleProps,
  showReset = false,
  onReset,
}: GroupCardProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Calculate visual styles based on depth
  const indentPx = depth * 16;
  // const borderColor =
  //   depth % 2 === 0
  //     ? "#90caf9" // blue-200
  //     : "#ce93d8"; // purple-200
  // const bgColor = depth === 0 ? "#fff" : "rgba(0, 0, 0, 0.02)";

  // Add a child criterion
  const addCriterion = () => {
    const newCriterion = createCriterionNode();
    onChange({
      ...group,
      children: [...group.children, newCriterion],
    });
  };

  // Add a sub-group
  const addSubGroup = () => {
    const newGroup = createGroupNode("AND");
    onChange({
      ...group,
      children: [...group.children, newGroup],
    });
  };

  // Update operator
  const handleOperatorChange = (event: SelectChangeEvent<string>) => {
    onChange({ ...group, op: event.target.value as "AND" | "OR" | "NOT" });
  };

  // Update a child
  const handleChildChange = (
    index: number,
    updated: GroupNode | CriterionNode
  ) => {
    const newChildren = [...group.children];
    newChildren[index] = updated;
    onChange({ ...group, children: newChildren });
  };

  // Delete a child
  const handleChildDelete = (index: number) => {
    const newChildren = group?.children?.filter((_, i) => i !== index);
    onChange({ ...group, children: newChildren });
  };

  // Count criteria and groups
  const criteriaCount = group?.children?.filter(isCriterionNode)?.length;
  const groupCount = group?.children?.filter(isGroupNode)?.length;

  return (
    <Card
      sx={{
        position: "relative",
        // border: `1px solid ${borderColor}`,
        // backgroundColor: bgColor,
        borderRadius: "12px",
        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.25)",
        marginLeft: depth > 0 ? `${indentPx}px` : 0,
      }}
      data-testid={`group-card-${group.id}`}
    >
      <Box sx={{ p: 2 }}>
        {/* Group Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flex: 1,
              minWidth: 0,
            }}
          >
            {/* Drag Handle (for non-root groups) */}
            {/* {depth > 0 && dragHandleProps && (
              <Box
                {...dragHandleProps}
                sx={{
                  cursor: "grab",
                  "&:active": { cursor: "grabbing" },
                  color: "text.secondary",
                  "&:hover": { color: "text.primary" },
                }}
                data-testid={`handle-group-${group.id}`}
              >
                <GripVertical sx={{ fontSize: 20 }} />
              </Box>
            )} */}

            {/* Collapse/Expand */}
            <IconButton
              size="small"
              onClick={() => setIsCollapsed(!isCollapsed)}
              data-testid={`button-toggle-group-${group.id}`}
              sx={{ p: 0.5 }}
            >
              {isCollapsed ? (
                <ChevronRight sx={{ fontSize: 20 }} />
              ) : (
                <ChevronDown sx={{ fontSize: 20 }} />
              )}
            </IconButton>

            {/* Operator Selector */}
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={group.op}
                onChange={handleOperatorChange}
                data-testid={`select-group-operator-${group.id}`}
              >
                <MenuItem value="AND">AND</MenuItem>
                <MenuItem value="OR">OR</MenuItem>
                <MenuItem value="NOT">NOT</MenuItem>
              </Select>
            </FormControl>

            {/* Group Info Badge */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {criteriaCount > 0 && (
                <Chip
                  label={`${criteriaCount} ${
                    criteriaCount === 1 ? "criterion" : "criteria"
                  }`}
                  variant="outlined"
                  size="small"
                  sx={{ fontSize: "0.75rem" }}
                />
              )}
              {groupCount > 0 && (
                <Chip
                  label={`${groupCount} ${
                    groupCount === 1 ? "sub-group" : "sub-groups"
                  }`}
                  variant="outlined"
                  size="small"
                  sx={{ fontSize: "0.75rem" }}
                />
              )}
              {group?.children?.length === 0 && (
                <Typography
                  variant="caption"
                  sx={{ fontStyle: "italic", color: "text.secondary" }}
                >
                  Empty group
                </Typography>
              )}
            </Box>
            {showReset && group?.children?.length !== 0 && (
              <Box sx={{ marginLeft: "auto" }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onReset}
                  data-testid="button-reset-nested-criteria"
                  startIcon={<RestartAlt />}
                >
                  Reset
                </Button>
              </Box>
            )}
          </Box>

          {/* Delete Button (for non-root groups) */}
          {depth > 0 && onDelete && (
            <IconButton
              size="small"
              onClick={onDelete}
              data-testid={`button-delete-group-${group.id}`}
            >
              <Trash2 sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>

        {/* Group Content (collapsible) */}
        <Collapse in={!isCollapsed}>
          <>
            {/* Children */}
            {group?.children?.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  mb: 2,
                }}
              >
                {group?.children?.map((child, index) => {
                  if (isCriterionNode(child)) {
                    return (
                      <CriteriaRow
                        key={child.id}
                        criterion={child}
                        studyId={studyId}
                        onChange={(updated) =>
                          handleChildChange(index, updated)
                        }
                        onDelete={() => handleChildDelete(index)}
                        platformType={platformType}
                      />
                    );
                  } else {
                    return (
                      <GroupCard
                        key={child.id}
                        group={child}
                        studyId={studyId}
                        depth={depth + 1}
                        onChange={(updated) =>
                          handleChildChange(index, updated)
                        }
                        onDelete={() => handleChildDelete(index)}
                      />
                    );
                  }
                })}
              </Box>
            )}

            {/* Add Actions */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                pt: 1.5,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={addCriterion}
                startIcon={<PlusCircle />}
                data-testid={`button-add-criterion-${group.id}`}
              >
                Add Criterion
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={addSubGroup}
                startIcon={<PlusCircle />}
                data-testid={`button-add-subgroup-${group.id}`}
              >
                Add Sub-Group
              </Button>
            </Box>
          </>
        </Collapse>
      </Box>
    </Card>
  );
}
